/**
 * Patient Indicators Component
 *
 * Horizontal row of patient context indicators
 */

import React from 'react';
import { Box } from '@mui/material';

import type { UseIndicatorHoverReturn } from '../hooks/useIndicatorHover';

import {
  PatientAllergiesIndicator,
  PatientDiagnosesIndicator,
  PatientLastEncounterIndicator,
  PatientMedicationsIndicator,
  PatientTreatmentPlanIndicator,
} from './index';
import type { TreatmentPlanData } from './PatientTreatmentPlanCard';

export interface PatientIndicatorsProps {
  medicationsHover: UseIndicatorHoverReturn;
  diagnosesHover: UseIndicatorHoverReturn;
  allergiesHover: UseIndicatorHoverReturn;
  lastEncounterHover: UseIndicatorHoverReturn;
  treatmentPlanHover?: UseIndicatorHoverReturn;
  treatmentPlan?: TreatmentPlanData | null;
  onMedicationsClick?: () => void;
  onDiagnosesClick?: () => void;
  onAllergiesClick?: () => void;
  onTreatmentPlanClick?: () => void;
}

export const PatientIndicators: React.FC<PatientIndicatorsProps> = ({
  medicationsHover,
  diagnosesHover,
  allergiesHover,
  lastEncounterHover,
  treatmentPlanHover,
  treatmentPlan,
  onMedicationsClick,
  onDiagnosesClick,
  onAllergiesClick,
  onTreatmentPlanClick,
}) => {
  const showTreatmentPlan = !!treatmentPlanHover;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Medications Indicator */}
      <Box
        ref={medicationsHover.indicatorRef}
        onMouseEnter={medicationsHover.handleMouseEnter}
        onMouseLeave={medicationsHover.handleMouseLeave}
      >
        <PatientMedicationsIndicator
          isHovered={medicationsHover.isHovered}
          onClick={onMedicationsClick}
        />
      </Box>

      {/* Diagnoses Indicator */}
      <Box
        ref={diagnosesHover.indicatorRef}
        onMouseEnter={diagnosesHover.handleMouseEnter}
        onMouseLeave={diagnosesHover.handleMouseLeave}
      >
        <PatientDiagnosesIndicator
          isHovered={diagnosesHover.isHovered}
          onClick={onDiagnosesClick}
        />
      </Box>

      {/* Allergies Indicator */}
      <Box
        ref={allergiesHover.indicatorRef}
        onMouseEnter={allergiesHover.handleMouseEnter}
        onMouseLeave={allergiesHover.handleMouseLeave}
      >
        <PatientAllergiesIndicator
          isHovered={allergiesHover.isHovered}
          onClick={onAllergiesClick}
        />
      </Box>

      {/* Last Encounter Indicator */}
      <Box
        ref={lastEncounterHover.indicatorRef}
        onMouseEnter={lastEncounterHover.handleMouseEnter}
        onMouseLeave={lastEncounterHover.handleMouseLeave}
      >
        <PatientLastEncounterIndicator isHovered={lastEncounterHover.isHovered} />
      </Box>

      {/* Treatment Plan Indicator */}
      {showTreatmentPlan && (
        <Box
          ref={treatmentPlanHover.indicatorRef}
          onMouseEnter={treatmentPlanHover.handleMouseEnter}
          onMouseLeave={treatmentPlanHover.handleMouseLeave}
        >
          <PatientTreatmentPlanIndicator
            isHovered={treatmentPlanHover.isHovered}
            mustBeUpdatedBy={treatmentPlan?.must_be_updated_by ?? null}
            onClick={onTreatmentPlanClick}
          />
        </Box>
      )}
    </Box>
  );
};
